import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { CopyId, ErrorState } from '@bb/ui';
import { ApiError } from '../../api/client.ts';
import { Can } from '../access/Can.tsx';
import { AlertRulesPanel } from '../alerts/AlertRulesPanel.tsx';
import { ApprovalPanel } from '../approvals/ApprovalPanel.tsx';
import { CommentsSidebar } from '../comments/CommentsSidebar.tsx';
import { useRecordView } from '../home/useRecordView.ts';
import { WhereUsedPanel } from '../operations/WhereUsedPanel.tsx';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { CampaignActions } from './CampaignActions.tsx';
import { CHANNEL_LABELS, DELIVERY_LABELS } from './labels.ts';
import { CampaignStatusBadge } from './CampaignStatusBadge.tsx';
import { CampaignTranslationsPanel } from './CampaignTranslationsPanel.tsx';
import { CampaignWizard } from './wizard/CampaignWizard.tsx';

/**
 * A campaign's status, what runs (published revision) and what waits (draft), its revisions and where it is used
 * (BB-96), and the wizard that composes, schedules, targets and launches it (BB-97); numbers are on its Analytics page (BB-104).
 */
export function CampaignPage() {
  const { environmentId, campaignId } = useParams({ from: '/env/$environmentId/campaigns/$campaignId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/campaigns/$campaignId' });
  const campaign = useQuery({ queryKey: ['campaigns', environmentId, 'one', campaignId], queryFn: () => api.getCampaign(environmentId, campaignId) });
  const revisions = useQuery({ queryKey: ['campaigns', environmentId, 'revisions', campaignId], queryFn: () => api.campaignRevisions(environmentId, campaignId) });
  useRecordView(environmentId, 'campaign', campaignId);
  if (campaign.isPending) return <p aria-busy="true">Loading campaign…</p>;
  if (campaign.isError) return <ErrorState title="Campaign could not be loaded" message={campaign.error instanceof ApiError ? campaign.error.message : 'Something went wrong.'} onRetry={() => void campaign.refetch()} />;
  const c = campaign.data;
  return (
    <section>
      <p>
        <Link to="/env/$environmentId/campaigns" params={{ environmentId }}>
          ← Campaigns
        </Link>
      </p>
      <h1>
        {c.name} <CampaignStatusBadge campaign={c} />
      </h1>
      {c.description && <p className="hint">{c.description}</p>}
      <CopyId label="API id" value={c.apiId} />
      <CampaignActions environmentId={environmentId} campaign={c} layout="toolbar" />
      {c.published && (
        <Can permission="reports.view">
          <p>
            <Link to="/env/$environmentId/campaigns/$campaignId/analytics" params={{ environmentId, campaignId }} search={{}}>
              Analytics
            </Link>{' '}
            <span className="hint">— KPIs, message performance, variants, funnel, retention and message activity</span>
          </p>
        </Can>
      )}
      <dl className="facts">
        <dt>Delivery</dt>
        <dd>{DELIVERY_LABELS[c.deliveryType]}</dd>
        <dt>Channels</dt>
        <dd>{c.channels.map((ch) => CHANNEL_LABELS[ch]).join(', ')}</dd>
        <dt>Running version</dt>
        <dd>{c.published ? `Revision ${c.published.revision}, published ${formatWhen(c.published.publishedAt)}${c.published.publishedBy?.email ? ` by ${c.published.publishedBy.email}` : ''}` : 'Not launched yet'}</dd>
        <dt>Draft</dt>
        <dd>{c.draft ? 'Unpublished changes are waiting for launch' : 'No unpublished changes'}</dd>
        {c.startAt && (
          <>
            <dt>Starts</dt>
            <dd>{formatWhen(c.startAt)}</dd>
          </>
        )}
        {c.endAt && (
          <>
            <dt>Stop date</dt>
            <dd>{formatWhen(c.endAt)}</dd>
          </>
        )}
        <dt>Last sent</dt>
        <dd>{c.lastSentAt ? formatWhen(c.lastSentAt) : '—'}</dd>
      </dl>
      <CampaignWizard environmentId={environmentId} campaign={c} />
      <ApprovalPanel environmentId={environmentId} campaign={c} />
      <CampaignTranslationsPanel environmentId={environmentId} campaign={c} />
      <h2>Revisions</h2>
      {revisions.data && (
        <ol aria-label="Revisions" reversed>
          {revisions.data.items.map((r) => (
            <li key={r.revision}>
              Revision {r.revision}: {r.published ? `published ${formatWhen(r.publishedAt!)}${r.publishedBy?.email ? ` by ${r.publishedBy.email}` : ''}` : 'draft'}
            </li>
          ))}
        </ol>
      )}
      <AlertRulesPanel environmentId={environmentId} objectType="campaign" objectId={campaignId} />
      <WhereUsedPanel environmentId={environmentId} objectType="campaign" objectId={campaignId} />
      <CommentsSidebar environmentId={environmentId} objectType="campaign" objectId={campaignId} />
    </section>
  );
}
